import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EmailIcon from '@mui/icons-material/Email'
import PhoneIcon from '@mui/icons-material/Phone'
import WorkIcon from '@mui/icons-material/Work'
import GroupIcon from '@mui/icons-material/Group'
import PreviewIcon from '@mui/icons-material/Preview'
import { Avatar, Box, Button, Chip, Divider, FormControlLabel, Grid, Paper, Switch, Typography } from '@mui/material'

import clsx from 'clsx'
import React from 'react'
import { UserRow } from '../../../admin/user-manager/um_scopes'
import { ProfileScope } from '../../up_scopes'
import ProfileCard from './profile-card'

export type UserProfileProps = {
    className?: string
    style?: React.CSSProperties
    key?: string
    scope: ProfileScope
    handleOpenGroupProfile: (groupId: number) => void
    handleOpenChangePassword: () => void
}

function UserProfile({ scope, className, style, handleOpenGroupProfile, handleOpenChangePassword }: UserProfileProps) {
    const user = scope.profile || ({} as UserRow)
    const renderBooleanIcon = (value: boolean) =>
        value ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />

    return (
        <ProfileCard className={clsx(className)} style={style}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 4 }}>
                <Avatar sx={{ width: 64, height: 64 }}>{user.full_name ? user.full_name[0] : 'U'}</Avatar>
                <Box>
                    <Typography variant="h5">{user.full_name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                        <Typography variant="body1">{user.name}</Typography>
                    </Box>
                    <Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={handleOpenChangePassword}>
                        Alterar senha
                    </Button>
                </Box>
                <Box sx={{ alignSelf: 'stretch' }}>
                    {user.fone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography variant="body1">{user.fone}</Typography>
                        </Box>
                    )}
                    {user.position && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <WorkIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography variant="body1">{user.position}</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            <Grid
                container
                spacing={2}
                sx={{
                    mb: 3
                }}
            >
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 'bold',
                                mr: 1
                            }}
                        >
                            Pode exportar dados:
                        </Typography>
                        {renderBooleanIcon(user.can_export_data)}
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    {user.can_export_data && (
                        <>
                            <strong>Limite de exportação aplicado de:</strong>{' '}
                            {user.export_limit_reference === 'GROUP' ? 'Grupo Preferencial' : 'Usuário'}
                        </>
                    )}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    {user.user_manager && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 'bold',
                                    mr: 1
                                }}
                            >
                                User Manager:
                            </Typography>
                            {renderBooleanIcon(user.user_manager)}
                        </Box>
                    )}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    {user.can_export_data && user.export_limit_reference === 'USER' && (
                        <>
                            <strong>Limite de exportação do usuário:</strong> {user.limit_export_quantity}
                        </>
                    )}
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
                Permissões
            </Typography>
            <Grid container spacing={2}>
                {[
                    { key: 'the_map', label: 'The Map' },
                    { key: 'the_consumer_market_ia', label: 'Mercado IA - Busca com IA' },
                    { key: 'tc_companies', label: 'The Connection - Empresas' },

                    { key: 'the_acting', label: 'The Acting' },
                    { key: 'tcm_companies', label: 'Mercado IA - Empresas' },
                    { key: 'tc_partners', label: 'The Connection - Sócios' },

                    { key: 'the_acting_print', label: 'The Acting Print' },
                    { key: 'tcm_partners', label: 'Mercado IA - Sócios' },
                    { key: 'tc_doctors', label: 'The Connection - Médicos' },

                    { key: 'the_connection', label: 'The Connection' },
                    { key: 'tcm_doctors', label: 'Mercado IA - Médicos' },
                    { key: 'tc_relationships', label: 'The Connection - Vínculos' },
                    { key: 'tcm_enchente_rs_24', label: 'The Connection - Filtro Enchente RS 2024' },

                    { key: 'the_consumer_market', label: 'Mercado IA' },
                    { key: 'tcm_relationships', label: 'Mercado IA - Vínculos' },
                    { key: 'mercado_vet', label: 'Mercado Veterinário' },
                    { key: 'analise_desempenho', label: 'Análise de Desempenho' }
                ].map((item) => (
                    <Grid key={item.key} size={{ xs: 12, sm: 4, md: 4 }}>
                        <FormControlLabel
                            control={<Switch checked={!!user[item.key as keyof UserRow]} readOnly color="primary" />}
                            label={item.label}
                        />
                    </Grid>
                ))}
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
                Grupos
            </Typography>
            <Grid container spacing={2}>
                {user.groups?.map((group) => (
                    <Grid key={group.id} size={{ xs: 12, sm: 6, md: 4 }}>
                        <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <GroupIcon fontSize="small" sx={{ mr: 1 }} />
                                <Box>
                                    <Typography variant="subtitle1">{group.name}</Typography>
                                </Box>
                            </Box>

                            <Typography variant="body2">Limite de exportação: {group.limit_export_quantity}</Typography>
                            {user.can_export_data &&
                                user.export_limit_reference === 'GROUP' &&
                                user.preferred_group_id === group.id && (
                                    <Chip label="Grupo Preferencial" color="success" size="small" sx={{ mt: 1 }} />
                                )}
                            {user.id === group.admin_id && (
                                <>
                                    <Chip label="Administrador" color="secondary" size="small" sx={{ mt: 1, ml: 1 }} />
                                    <Button
                                        variant="outlined"
                                        startIcon={<PreviewIcon fontSize="small" />}
                                        onClick={() => handleOpenGroupProfile(group.id!)}
                                        size="small"
                                        sx={{ mt: 1, ml: 1 }}
                                    >
                                        Detalhes do grupo
                                    </Button>
                                </>
                            )}
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </ProfileCard>
    )
}
export default UserProfile
